import { ChangeDetectorRef, Component } from '@angular/core';
import { MethodTabs, SelectHelpComponent } from '../select-help/select-help.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EpApplicationService, EpLocalStorageService, EpShellService, IEpSelectionListItem } from '@epicor/ux-platform';
import { SwaggerDocService } from '../services/swagger-doc.service';
import { HeaderService } from '../services/header.service';
import { HelpPageService } from '../services/help-page.service';
import { HelpType } from '../models/help-type';
import { RestVersions } from '../models/rest-versions';

@Component({
  selector: 'app-select-service',
  templateUrl: '../select-help/select-help.component.html',
  styleUrls: ['../select-help/select-help.component.scss']
})
export class SelectServiceComponent extends SelectHelpComponent {
  service: string;
  method: string;
  helpType: HelpType;

  constructor(
    route: ActivatedRoute,
    router: Router,
    epApplicationService: EpApplicationService,
    swaggerDocService: SwaggerDocService,
    cdr: ChangeDetectorRef,
    epShellService: EpShellService,
    headerService: HeaderService,
    helpPageService: HelpPageService,
    epLocalStorageService: EpLocalStorageService
  ) {
    super(
      route,
      router,
      epApplicationService,
      swaggerDocService,
      cdr,
      epShellService,
      headerService,
      helpPageService,
      epLocalStorageService
    );
  }

  processRouteParams(params: Params) {
    this.method = '';

    let type = params['type'];

    if (!type) {
      type = HelpType.OData;
    }
    this.helpType = type;

    const service = params['service'];
    const method = params['method'];

    if (service) {
      this.service = service;
    }

    if (method) {
      if (method === '/') {
        this.method = ''; // special case for service document
      } else {
        this.method = method;
      }
    }
  }

  isTabHidden(panelHelpType: HelpType): boolean {
    return panelHelpType !== HelpType.OData && panelHelpType !== HelpType.Methods;
  }

  showHelpUI(item): void {
    this.router.navigate(['select-service', this.version, this.helpType, this.service, item.event.text]);
  }

  searchMethod(item) {
    this.currentSearch = item.event.toLowerCase();
    this.panelListModel.items = this.panelListModel.allItems.filter(me => me.id.indexOf(this.currentSearch) >= 0);
  }

  shouldShowSelectItemMessage(): boolean {
    if (this.helpType === HelpType.OData) {
      return false;
    }

    return !(this.version && this.method);
  }

  selectItemMessage(): string {
    return this.appStrings.selectItemFromTheList;
  }

  selectHelpForType() {
    switch (this.helpType) {
      case HelpType.OData:
        this.tabModel.data[0].selected = true;
        this.tabModel.data[1].selected = false;
        break;
      case HelpType.Methods:
        this.tabModel.data[0].selected = false;
        this.tabModel.data[1].selected = true;
        break;
    }

    this.showMethodHelp();
  }

  showMethodHelp() {
    this.helpTitle = this.buildTitle();
    this.epShellService.setTitle(this.helpTitle);

    this.showList(this.service, this.version, this.helpType);

    this.swaggerDocService.getServiceSwaggerDoc(this.service, this.version, this.helpType).subscribe({
      next: doc => {
        const methodName =
          this.version === RestVersions.V2 ? `/{currentCompany}/${this.service}/${this.method}` : `/${this.method}`;

        const specMethod = {};

        specMethod[methodName] = doc.paths[methodName];
        const partialSpec = this.getPartialDoc(doc, specMethod);
        this.showSwaggerUI({ currentSpec: partialSpec });
      }
    });
  }

  showList(service, version, type) {
    this.swaggerDocService.getServiceSwaggerDoc(service, version, type).subscribe({
      next: doc => {
        this.panelListModel.allItems = Object.keys(doc.paths).map(path => {
          const ind = path.lastIndexOf('/') + 1;
          const item = path.substring(ind);
          const descr = this.getMethodDescription((<any>Object.values(doc.paths[path])[0]).description);
          return <IEpSelectionListItem>{
            id: item.toLowerCase(),
            text: item.length > 0 ? item : '/',
            description: descr,
            selected: false
          };
        });
        this.panelListModel.items = this.currentSearch
          ? this.panelListModel.allItems.filter(me => me.id.indexOf(this.currentSearch) >= 0)
          : [...this.panelListModel.allItems];
      }
    });
  }

  private getMethodDescription(description: any): string {
    if (!description) {
      return '';
    }
    const ind = description.indexOf('<');
    if (ind > 0) {
      return description.substring(0, ind);
    }
    return description;
  }

  versionComboHidden() {
    return false;
  }

  companyComboHidden() {
    return true;
  }

  onTabSelect(item) {
    if (item.event.title === MethodTabs.OData) {
      this.helpType = HelpType.OData;
      this.currentSearch = undefined;
      this.tabModel.data[0].selected = true;
      this.tabModel.data[1].selected = false;
      this.showList(this.service, this.version, this.helpType);
    } else if (item.event.title === MethodTabs.RPC) {
      this.helpType = HelpType.Methods;
      this.currentSearch = undefined;
      this.tabModel.data[0].selected = false;
      this.tabModel.data[1].selected = true;
      this.showList(this.service, this.version, this.helpType);
    }

    setTimeout(() => {
      this.setHeights(window.innerHeight);
    });
  }

  changeCompany(item): void {}

  changeVersion(item): void {
    const version = item.event;
    this.showList(this.service, version, this.helpType);
  }

  getLegacyHelpPageLink(): string {
    if (!this.service && !this.method) {
      return '';
    }
    let helpUrl = this.epApplicationService.sessionService.serverUrl;

    helpUrl += `/api/help/${this.version}/odata/${this.service}/index`;

    if (this.helpType === HelpType.OData) {
      helpUrl += '?urls.primaryName=OData';
    } else {
      helpUrl += '?urls.primaryName=Custom%20methods';
    }
    return helpUrl;
  }

  getSwaggerDocLink(): string {
    return this.swaggerDocService.buildServiceProtocolUrl(this.service, this.version, this.helpType);
  }

  shouldShowLinks(): boolean {
    return !!this.service;
  }

  buildTitle(): string {
    if (this.method) {
      return `${this.service} - ${this.method}`;
    } else {
      return this.service;
    }
  }
}
